import React from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Movie as MovieIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  ExitToApp as ExitToAppIcon,
  ChevronLeft as ChevronLeftIcon,
  ChatBubble as ChatBubbleIcon,
  AdsClick as AdsClickIcon,
  Casino as CasinoIcon,
  SwapHoriz as SwapHorizIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';

const drawerWidth = 240;

const AdminLayout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const history = useHistory();
  const location = useLocation();
  
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [drawerOpen, setDrawerOpen] = React.useState(!isMobile);

  // 后台菜单选项
  const adminMenuItems = [
    { text: '系统数据', icon: <DashboardIcon />, path: '/admin' },
    // { text: '剧集管理', icon: <MovieIcon />, path: '/admin/dramas' }, // 隐藏剧集管理菜单
    { text: '短剧管理', icon: <MovieIcon />, path: '/admin/drama-management' },
    { text: '剧集生成', icon: <SettingsIcon />, path: '/admin/drama-generator' },
    { text: '海报生成', icon: <MovieIcon />, path: '/admin/poster-generator' },
    { text: '用户管理', icon: <PeopleIcon />, path: '/admin/mongo-users' },
    { text: '彩票管理', icon: <CasinoIcon />, path: '/admin/lottery-management' },
    { text: '引流管理', icon: <SwapHorizIcon />, path: '/admin/traffic-management' },
    { text: '流量统计', icon: <BarChartIcon />, path: '/admin/traffic-statistics' },
    { text: '评论管理', icon: <ChatBubbleIcon />, path: '/admin/comments' },
    { text: '广告管理', icon: <AdsClickIcon />, path: '/admin/advertisements' },
    { text: '配置管理', icon: <SettingsIcon />, path: '/admin/config' },
  ];

  // 处理菜单点击
  const handleMenuClick = (path) => {
    history.push(path);
    setMobileOpen(false);
    setAnchorEl(null);
  };

  // 处理移动端菜单打开
  const handleMobileMenuOpen = () => {
    setMobileOpen(true);
  };

  // 处理移动端菜单关闭
  const handleMobileMenuClose = () => {
    setMobileOpen(false);
  };

  // 处理抽屉开关
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  // 处理用户菜单打开
  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // 处理用户菜单关闭
  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  // 处理退出登录
  const handleLogout = () => {
    handleUserMenuClose();
    // 清除用户登录状态
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('user');
    history.push('/login');
  };

  // 渲染侧边栏内容
  const renderDrawerContent = () => (
    <Box>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          后台管理
        </Typography>
        {!isMobile && (
          <IconButton onClick={handleDrawerToggle} sx={{ ml: 'auto' }}>
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Toolbar>
      <List>
        {adminMenuItems.map((item) => (
          <ListItem 
            button 
            key={item.text}
            onClick={() => handleMenuClick(item.path)}
            selected={location.pathname === item.path}
            sx={{
              '&.Mui-selected': {
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              },
            }}
          >
            <ListItemIcon sx={{ color: location.pathname === item.path ? 'white' : theme.palette.primary.main }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* 顶部导航栏 */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerOpen ? drawerWidth : 0}px)` },
          ml: { md: `${drawerOpen ? drawerWidth : 0}px` },
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          boxShadow: '0 4px 20px rgba(99, 102, 241, 0.3)',
          borderRadius: 0,
        }}
      >
        <Toolbar>
          {!isMobile && drawerOpen && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}
          
          {(!drawerOpen || isMobile) && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => isMobile ? setMobileOpen(true) : setDrawerOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            {adminMenuItems.find(item => location.pathname === item.path)?.text || '后台管理'}
          </Typography>
          
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleUserMenuOpen}
            color="inherit"
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            <PeopleIcon />
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleUserMenuClose}
            sx={{
              '& .MuiPaper-root': {
                borderRadius: 2,
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <MenuItem onClick={handleLogout} sx={{
              '&:hover': {
                backgroundColor: 'rgba(99, 102, 241, 0.1)',
              },
            }}>
              <ListItemIcon><ExitToAppIcon fontSize="small" /></ListItemIcon>
              <ListItemText>退出登录</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* 侧边栏 */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleMobileMenuClose}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
              boxShadow: '2px 0 10px rgba(0, 0, 0, 0.08)',
            },
          }}
        >
          {renderDrawerContent()}
        </Drawer>
      ) : (
        <Drawer
          variant="persistent"
          anchor="left"
          open={drawerOpen}
          sx={{
            width: drawerOpen ? drawerWidth : 0,
            flexShrink: 0,
            '& .MuiDrawer-paper': { 
              width: drawerWidth, 
              boxSizing: 'border-box',
              background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
              borderRight: '1px solid #e2e8f0',
            },
          }}
        >
          {renderDrawerContent()}
        </Drawer>
      )}

      {/* 主内容区域 */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerOpen ? drawerWidth : 0}px)` },
          marginLeft: { md: `${drawerOpen ? 0 : `-${drawerWidth}px`}` },
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
          }),
          marginTop: '64px', // 为顶部导航栏预留空间
          minHeight: 'calc(100vh - 64px)', // 确保内容区域高度至少为视口高度减去导航栏高度
          background: 'linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 100%)', // 稍微深一点的背景色
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default AdminLayout;